import React from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Check, X } from 'lucide-react'
import { StyledModalButton } from '@/components/ui/styled-modal'

interface UpdateConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  transactionCount: number
  selectedFields: string[]
}

export function UpdateConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  transactionCount,
  selectedFields
}: UpdateConfirmationDialogProps) {
  // Handle escape key and backdrop click
  React.useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    
    window.addEventListener('keydown', handleEscapeKey)
    return () => window.removeEventListener('keydown', handleEscapeKey)
  }, [isOpen, onClose])

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-[50000] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div className="w-full max-w-md transform transition-all" onClick={(e) => e.stopPropagation()}>
        <div className="relative bg-white dark:bg-[#182438] border border-[#2F2F2F]/10 dark:border-[#333840] rounded-xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-[#2F2F2F]/10 dark:border-[#333840] bg-gradient-to-r from-[#E53935]/10 to-[rgba(8,31,61,0.05)] dark:from-[#E53935]/20 dark:to-[rgba(8,31,61,0.1)]">
            <h2 className="text-xl font-semibold text-[#1C1C1C] dark:text-[#E8E8E8] flex items-center">
              <AlertTriangle className="h-5 w-5 text-[#E53935] mr-2" />
              Confirm Mass Update
            </h2>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-[#2F2F2F] dark:text-[#A3A3A3]">
              You are about to update {transactionCount} {transactionCount === 1 ? 'transaction' : 'transactions'} with the following changes:
            </p>
            
            <div className="bg-gradient-to-r from-[#439476]/10 to-[rgba(8,31,61,0.05)] dark:from-[#439476]/20 dark:to-[rgba(8,31,61,0.1)] rounded-lg p-4 border border-[#439476]/20 dark:border-[#439476]/30">
              <ul className="space-y-2">
                {selectedFields.map((field) => (
                  <li key={field} className="flex items-start gap-2 text-sm text-[#1C1C1C] dark:text-[#E8E8E8]">
                    <span className="w-1.5 h-1.5 bg-[#439476] rounded-full mt-2 flex-shrink-0"></span>
                    {field}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-gradient-to-r from-[#E53935]/10 to-[rgba(8,31,61,0.05)] dark:from-[#E53935]/20 dark:to-[rgba(8,31,61,0.1)] rounded-lg p-4 border border-[#E53935]/20 dark:border-[#E53935]/30">
              <p className="text-sm text-[#E53935] dark:text-[#EF5350] flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                This action cannot be undone. Are you sure you want to continue?
              </p>
            </div>
          </div>

          <div className="flex gap-3 p-4 border-t border-[#2F2F2F]/10 dark:border-[#333840] bg-gradient-to-r from-[#F3F4F6]/50 to-transparent dark:from-[#1E2B42]/50 dark:to-transparent">
            <StyledModalButton
              variant="outline"
              onClick={onClose}
              icon={<X className="w-4 h-4" />}
              fullWidth
            >
              Cancel
            </StyledModalButton>
            <StyledModalButton
              variant="danger"
              onClick={onConfirm}
              icon={<Check className="w-4 h-4" />}
              fullWidth
            >
              Update
            </StyledModalButton>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level, just like StyledModal
  return createPortal(modalContent, document.body);
} 